import React, {ChangeEvent, useMemo, useState, useCallback} from 'react';
import styles from './textInput.module.scss';
import { ValidInputRegTest } from '../../constants/index';

export type TextInputChange = (value: string) => void;

type Props = {
  label: string | (() => React.ReactNode);
  id: string;
  value: string;
  placeholder?: string;
  className?: string;
  onChange: TextInputChange;
  inline?: boolean;
  hasError?: boolean;
  subTitle?: string | React.ReactNode;
  readonly?: boolean;
  autoFocus?: boolean;
  maxLength: number;
  allowSpecialCharacters?: boolean;
  showValidationMessage?: boolean;
};

export const TextInputWithLengthLimit = (props: Props) => {
  const { label, id, placeholder, value, onChange, className, inline, hasError, subTitle, readonly, autoFocus, maxLength, allowSpecialCharacters, showValidationMessage } = props;
  const [currentLength, setCurrentLength] = useState(value.length ?? 0);
  const [showValidationError, setShowValidationError] = useState(false);

  const handleChange = useCallback((e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const inputValue = `${e.target.value}`;
    
    if (allowSpecialCharacters || ValidInputRegTest.test(inputValue)) {
      // If the input is valid, update the value normally
      setCurrentLength(inputValue?.length ?? 0);
      onChange(inputValue);
      if (showValidationError) {
        setShowValidationError(false);
      }
    } else {
      // If the input contains invalid characters, strip them out
      const strippedValue = inputValue.split('').filter(char => ValidInputRegTest.test(char)).join('');
      
      // Update the value with the stripped version
      setCurrentLength(strippedValue?.length ?? 0);
      onChange(strippedValue);
      
      // Show validation message if enabled
      if (showValidationMessage) {
        setShowValidationError(true);
      }
    }
  }, [allowSpecialCharacters, onChange, showValidationError, showValidationMessage]);

  const classes = useMemo(() => {
    const classes = [styles.wrapper, className];
    if (inline) {
      classes.push(styles.inline);
    }
    if (value) {
      classes.push(styles.hasValue);
    }
    if (hasError || currentLength > maxLength || showValidationError) {
      classes.push(styles.error);
    }
    return classes.join(' ').trim();
  }, [className, inline, value, hasError, currentLength, maxLength, showValidationError]);

  const notificationClasses = useMemo(() => {
    const classes = [styles.notification];
    if (currentLength > maxLength) {
      classes.push(styles.red);
    }
    return classes.join(' ').trim();
  }, [currentLength, maxLength]);

  return (
    <div className={classes}>
      {typeof label === 'function' ? label() : <label className={styles.label} htmlFor={id}>{label}</label>}
      <div className={notificationClasses}>{maxLength - currentLength} character{maxLength - currentLength === 1 ? '' : 's'} {currentLength === 0 ? 'max' : 'left'}</div>
      <div className={styles.content}>
        <input autoFocus={autoFocus} readOnly={readonly} onChange={handleChange} value={value} placeholder={placeholder} className={styles.input} id={id} type={'text'}/>
        {showValidationError ? (
          <div className={styles.subtitle}>
            <p>For security, we automatically strip characters that are not permitted. Please use only the following characters:</p>
            <ul className={styles.errorPoints}>
              <li>A → Z</li>
              <li>a → z</li>
              <li>0, 1 → 9</li>
              <li>; : " ' ` . + = , / ? & @ !</li>
            </ul>
            <p>If you’re having troubles or would like us to add a character, please reach out to Open Parachute support.</p>
          </div>
          ) : (
          subTitle ? <div className={styles.subtitle}>{subTitle}</div> : null 
        )}
      </div>
    </div>
  );
};
